import { defineStore } from 'pinia'
import * as liquidationApi from '../services/api/liquidation'
import { IApproval } from './staff'

export interface ILiquidationData {
  id: number
  status: string
  note: string
  reason: string
  approvals: Array<IApproval>
  liquidation_no: string
  images: Array<any>
  assignee_id: number
  car_id: number
  customer_id: number
  assignee: any
  car: any
  customer: any
  created_at: string
}

export const useLiquidationStore = defineStore('liquidation', {
  state: () => ({
    liquidation: {
      id: 0,
      status: '',
      note: '',
      reason: '',
      approvals: [],
      liquidation_no: '',
      assignee_id: 0,
      car_id: 0,
      customer_id: 0,
      images: [],
      assignee: {},
      car: {},
      customer: {},
      created_at: '',
    } as ILiquidationData,
    isLoading: false,
  }),

  actions: {
    setLiquidation(data: ILiquidationData) {
      this.liquidation = data
    },

    setLoading(value: boolean) {
      this.isLoading = value
    },

    async getLiquidation(id: number) {
      try {
        this.setLoading(true)
        const { data } = await liquidationApi.get(id)
        this.setLiquidation(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async updateLiquidation(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await liquidationApi.update(this.liquidation.id, payload)
        this.setLiquidation(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async applyLiquidation() {
      try {
        this.setLoading(true)
        const { data } = await liquidationApi.apply(this.liquidation.id)
        this.setLiquidation(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async rejectLiquidation(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await liquidationApi.reject(this.liquidation.id, payload)
        this.setLiquidation(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async remakeLiquidation(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await liquidationApi.remake(this.liquidation.id, payload)
        this.setLiquidation(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async approveLiquidation() {
      try {
        this.setLoading(true)
        const { data } = await liquidationApi.approve(this.liquidation.id)
        this.setLiquidation(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async completeLiquidation(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await liquidationApi.complete(this.liquidation.id, payload)
        this.setLiquidation(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async exportDocxLiquidation(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await liquidationApi.exportDocx(this.liquidation.id, payload)
        this.setLoading(false)
        return data
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },
  },
})

export const useLiquidationListStore = defineStore('liquidationList', {
  state: () => ({
    liquidations: [] as ILiquidationData[],
    count: 0,
    isLoading: false,
    total: 0,
  }),

  actions: {
    setCount(data: number) {
      this.count = data
    },

    addLiquidation(data: ILiquidationData) {
      this.liquidations.push(data)
    },

    setLiquidations(data: ILiquidationData[]) {
      this.liquidations = data
    },

    setLoading(value: boolean) {
      this.isLoading = value
    },

    setTotal(value: number) {
      this.total = value
    },

    async countLiquidations() {
      try {
        this.setLoading(true)
        const { data } = await liquidationApi.count()
        this.setCount(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async createLiquidation(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await liquidationApi.create(payload)
        this.addLiquidation(data)
        this.setLoading(false)
        return data
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async listLiquidations(params: any) {
      try {
        this.setLoading(true)
        const result = await liquidationApi.list(params)
        const { data, total } = result as any
        this.setLiquidations(data)
        this.setTotal(total)
        this.setLoading(false)
        return result
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async listAllLiquidations(params: any) {
      try {
        this.setLoading(true)
        const result = await liquidationApi.listAll(params)
        const { data, total } = result as any
        this.setLiquidations(data)
        this.setTotal(total)
        this.setLoading(false)
        return result
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },
  },
})
